import Cart from 'cart-store';

/*
  Adds white-glove installation alongside the product it is attached to.

  Installation was priced by distance and gated behind a zip lookup; it is now
  priced per product, so this component only has one job — intercept the add so
  the installation variant lands in the same cart request as the product.

  One installation per order, so a second eligible product does not add a
  second installation line. If the script never runs the buy box is untouched
  and the product still adds on its own — we would rather lose the add-on than
  the sale.
*/
if (!customElements.get('ls-install-estimator')) {
  customElements.define(
    'ls-install-estimator',
    class LsInstallEstimator extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        this.variantId = this.dataset.variant;
        if (!this.form || !this.variantId) return;

        this.onSubmit = this.onSubmit.bind(this);
        /* Capture phase so we run instead of the theme's product-form, which
           can only add one line. */
        this.form.addEventListener('submit', this.onSubmit, true);
      }

      disconnectedCallback() {
        if (this.form) this.form.removeEventListener('submit', this.onSubmit, true);
      }

      async fetchCart() {
        const res = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        return res.json();
      }

      /* Any installation line counts, not just this product's — the client
         wants one installation per order.

         Match on the installation product's handle, not on its title. Several
         real products have "Installation" in their own title (e.g. "PROforce
         660 - Proformance Hoops Installation"), and a title substring match
         treated those as an installation line: with one in the cart, every
         product added afterwards silently lost its installation. */
      async installationInCart() {
        const cart = await this.fetchCart();
        return (cart.items || []).some((item) => item.handle === 'installation');
      }

      async onSubmit(event) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const staged = this.form.closest('product-form')?.querySelector('staged-action');
        const data = Object.fromEntries(new FormData(this.form).entries());
        this.removeError();

        try {
          await this.stage(staged, 'DOING');

          /* Both lines carry a hidden marker so the cart can tell them apart
             later: ls-install-lock.js drops the installation line once the last
             product that needs it has gone. Underscore-prefixed properties are
             hidden from the customer and from checkout. Cart.add expects the
             `properties[Name]` form, not a nested object. */
          const items = [
            {
              id: data.id,
              quantity: Number(data.quantity) || 1,
              'properties[_installation_required]': 'true',
            },
          ];
          if (!(await this.installationInCart())) {
            items.push({
              id: this.variantId,
              quantity: 1,
              'properties[_installation_auto]': 'true',
            });
          }
          /* Ticked add-ons from ls-product-addons.liquid. Left unmarked, so
             ls-install-lock.js treats them as ordinary lines. */
          this.form.querySelectorAll('input[data-ls-addon]:checked').forEach((input) => {
            items.push({ id: input.value, quantity: 1 });
          });
          await Cart.add(items);

          await this.stage(staged, 'DONE');
          this.openCartDrawer();
        } catch (error) {
          await this.stage(staged, 'IDLE');
          this.showError(error.message);
        }
      }

      /* The button's tick animation is decoration. staged-action caches its
         tick element in its constructor and throws if it was not parsed yet,
         which would otherwise report a successful add as a failure. */
      async stage(staged, state) {
        if (!staged) return;
        try {
          await staged.setState(state);
        } catch (error) {
          /* ignore */
        }
      }

      openCartDrawer() {
        const drawer = document.querySelector('.modal--cart, .popup--cart-notification');
        if (!drawer) return;
        if (!drawer.shown && typeof drawer.open === 'function') drawer.open();
        else if (drawer.interval) {
          drawer.interval.reset();
          drawer.interval.start();
        }
      }

      removeError() {
        const existing = this.form.querySelector('.lsie-error');
        if (existing) existing.remove();
      }

      showError(message) {
        this.removeError();
        const button = this.form.elements.add;
        if (!button) return;
        const el = document.createElement('div');
        el.className = 'errors qty-error u-small lsie-error';
        el.textContent = message || 'Something went wrong. Please try again.';
        button.before(el);
      }
    },
  );
}
